import { useEffect, useRef, useState } from 'react';
import { FolderOpen, Paperclip, Pencil, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';
import { useI18n } from '../lib/i18n';
import { uploadFile } from '../lib/upload';
import { scrollBoxToEnd } from '../lib/scrollHelpers';
import { MentionTextarea } from './MentionTextarea';
import { Avatar } from './Avatar';
import { renderMentions } from '../lib/mentions';
import { useAuth } from '../lib/auth';

type Att = { key: string; name: string };
type Msg = { id: string; author: string | null; author_user_id: string | null; body: string; created_at: string; attachments?: Att[] };

/**
 * Case Overview (Claim Review) como CHAT: cualquier usuario escribe estrategias e
 * indicaciones tras leer la póliza y se ve quién dijo qué (avatar + nombre + fecha).
 */
export function CaseOverview({ claimId, readOnly = false }: { claimId: string; readOnly?: boolean }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const myId = me?.kind === 'tenant' ? String(me.user.id) : null; // solo el autor edita su nota
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  // Lightbox de la captura adjunta (click en la miniatura = verla grande).
  const [big, setBig] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const { fallo, marcar, limpiar } = useFallo();
  const load = () => api.get<{ messages: Msg[] }>(`/api/claims/${claimId}/case-overview`).then((r) => setMsgs(r.messages)).then(limpiar).catch(marcar);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId]);
  useEffect(() => { scrollBoxToEnd(endRef.current); }, [msgs.length]);

  async function send() {
    if (!body.trim() && files.length === 0) return;
    setBusy(true);
    try {
      const attachments = await Promise.all(files.map(async (f) => ({ key: await uploadFile(f, 'misc'), name: f.name })));
      await api.post(`/api/claims/${claimId}/case-overview`, { body: body.trim(), attachments });
      setBody(''); setFiles([]); await load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault(); setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length) setFiles((f) => [...f, ...dropped]);
  }
  async function saveEdit() {
    if (!editId || !editBody.trim()) return;
    setBusy(true);
    try { await api.put(`/api/claims/${claimId}/case-overview/${editId}`, { body: editBody.trim() }); setEditId(null); setEditBody(''); await load(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  return (
    /* id=case-overview: ancla del deep-link de las notificaciones de menciones en el Case Overview. */
    <div id="case-overview" className="card" style={{ display: 'grid', gap: 8 }}>
      {fallo && <NoCargo onRetry={load} compact />}
      <h2 className="card-title" style={{ margin: 0 }}><FolderOpen size={16} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('co.title')}</h2>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('co.subtitle')}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto', padding: '4px 0' }}>
        {msgs.length === 0 && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('co.empty')}</div>}
        {msgs.map((m) => (
          <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <Avatar userId={m.author_user_id} name={m.author} size={26} />
            <div style={{ flex: 1, minWidth: 0, background: 'var(--surface-2)', borderRadius: 10, padding: '7px 10px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <b style={{ fontSize: 'var(--fs-xs)' }}>{m.author || '—'}</b>
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{new Date(m.created_at).toLocaleString('en-US')}</span>
                <span style={{ flex: 1 }} />
                {!readOnly && editId !== m.id && myId && m.author_user_id === myId && (
                  <button className="btn ghost sm" style={{ padding: '0 6px', fontSize: 'var(--fs-2xs)' }} title={t('common.edit')} onClick={() => { setEditId(m.id); setEditBody(m.body); }}><Pencil size={12} strokeWidth={1.75} /></button>
                )}
              </div>
              {editId === m.id ? (
                <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
                  <MentionTextarea value={editBody} onChange={setEditBody} minHeight={40} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn sm" disabled={busy || !editBody.trim()} onClick={saveEdit}>{t('common.save')}</button>
                    <button className="btn ghost sm" onClick={() => { setEditId(null); setEditBody(''); }}>{t('common.cancel')}</button>
                  </div>
                </div>
              ) : (
                <>
                  {m.body && <div style={{ fontSize: 'var(--fs-sm)', whiteSpace: 'pre-wrap', marginTop: 2 }}>{renderMentions(m.body)}</div>}
                  {m.attachments && m.attachments.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                      {/* Las IMÁGENES se VEN (miniatura, click = grande), no un link (Paula 2026-08-14:
                          "and to see the screenshot instead of a link"). Lo demás sigue como chip. */}
                      {m.attachments.map((a, i) => {
                        const url = `/api/claims/${claimId}/case-overview/${m.id}/attachment?i=${i}`;
                        const esImagen = /\.(png|jpe?g|gif|webp|heic|bmp)$/i.test(a.name ?? '');
                        return esImagen ? (
                          <img key={i} src={url} alt={a.name} title={a.name} onClick={() => setBig(url)}
                            style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)', cursor: 'zoom-in' }} />
                        ) : (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                             className="badge" style={{ fontSize: 'var(--fs-2xs)', textDecoration: 'none', cursor: 'pointer' }}><Paperclip size={12} strokeWidth={1.75} /> {a.name}</a>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {!readOnly && (
        <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
          style={{ display: 'grid', gap: 6, border: dragOver ? '2px dashed var(--accent)' : '2px dashed transparent', borderRadius: 10, padding: dragOver ? 6 : 0, transition: 'border-color .15s' }}>
          {files.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {files.map((f, i) => (
                <span key={i} className="badge" style={{ fontSize: 'var(--fs-2xs)', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                  <Paperclip size={12} strokeWidth={1.75} /> {f.name}
                  <button className="btn ghost sm" style={{ padding: 0, lineHeight: 1 }} onClick={() => setFiles((arr) => arr.filter((_, j) => j !== i))}><X size={12} strokeWidth={1.75} /></button>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => { const fs = Array.from(e.target.files || []); if (fs.length) setFiles((arr) => [...arr, ...fs]); e.target.value = ''; }} />
            <button className="btn ghost sm" title={t('co.attach')} onClick={() => fileRef.current?.click()}><Paperclip size={14} strokeWidth={1.75} /></button>
            <div style={{ flex: 1 }}><MentionTextarea value={body} onChange={setBody} placeholder={t('co.placeholder')} minHeight={38} onEnter={send} /></div>
            <button className="btn sm" disabled={busy || (!body.trim() && files.length === 0)} onClick={send}>{t('common.send')}</button>
          </div>
          {dragOver && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', textAlign: 'center' }}>{t('co.dropHere')}</div>}
        </div>
      )}
      {big && (
        <div onClick={() => setBig(null)} role="dialog" aria-modal="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 4000, display: 'grid', placeItems: 'center', cursor: 'zoom-out' }}>
          <img src={big} alt="" style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}
